import { defineField, defineType } from 'sanity'
import VehicleInput from '../../components/VehicleInput'
import AlbumURL from '../../components/AlbumURL'
import getPageInfo, { seoGroup } from '../objects/pageInfo'
import { generateImageAltTags } from '../../utils/generateImageAltTags'

// Required attributes that must be filled
const attributes = [
  { name: 'fitment', title: 'Fitment Type', type: 'array', typeto: 'fitment' },
  { name: 'color', title: 'Vehicle Color', typeto: 'color' },
  { name: 'design', title: 'Wheel Design', typeto: 'design' },
  { name: 'finish', title: 'Wheel Finish', typeto: 'finish' },
  { name: 'profile', title: 'Wheel Profile', typeto: 'profile' },
  { name: 'diameter', title: 'Wheel Diameter', typeto: 'diameter' },
  { name: 'lug', title: 'Wheel Lug Pattern', typeto: 'lug' },
]

// Optional attributes
const attributesOpt = [
  { name: 'frontWidth', title: 'Front Wheel Width', typeto: 'width' },
  { name: 'frontOffset', title: 'Front Wheel Offset', typeto: 'offset' },
  { name: 'rearWidth', title: 'Rear Wheel Width', typeto: 'width' },
  { name: 'rearOffset', title: 'Rear Wheel Offset', typeto: 'offset' },
  { name: 'frontSpacer', title: 'Front Wheel Spacer', typeto: 'spacer' },
  { name: 'rearSpacer', title: 'Rear Wheel Spacer', typeto: 'spacer' },
  { name: 'tireBrand', title: 'Tire Brand/Model', typeto: 'tireBrand' },
  { name: 'tireType', title: 'Tire Type', typeto: 'tireType' },
  {
    name: 'frontTireSize',
    title: 'Front Tire Size',
    typeto: 'tireSize',
    options: {
      disableNew: true,
      // TODO: Add filter function after client migration
      // filter: async ({ document }) => {
      //   const client = useClient()
      //   const name = await client.fetch(`*[_id == $id][0].name`, {
      //     id: document?.diameter?._ref,
      //   })
      //   const diameter = name.split('/').shift()
      //   return {
      //     filter: 'diameter->name == $diameter',
      //     params: { diameter },
      //   }
      // },
    },
    hidden: ({ document }: any) => !document?.diameter?._ref,
  },
  {
    name: 'rearTireSize',
    title: 'Rear Tire Size',
    typeto: 'tireSize',
    options: {
      disableNew: true,
      // TODO: Add filter function after client migration
      // filter: async ({ document }) => {
      //   const client = useClient()
      //   const name = await client.fetch(`*[_id == $id][0].name`, {
      //     id: document?.diameter?._ref,
      //   })
      //   const diameter = name.split('/').pop()
      //   return {
      //     filter: 'diameter->name == $diameter',
      //     params: { diameter },
      //   }
      // },
    },
    hidden: ({ document }: any) => !document?.diameter?._ref,
  },
  {
    name: 'requiredMod',
    title: 'Required Mods',
    type: 'array',
    typeto: 'requiredMod',
  },
  {
    name: 'centerCap',
    title: 'Center Cap Color',
    typeto: 'centerCap',
    // TODO: Add initialValue function after client migration
    // initialValue: async () => {
    //   const client = useClient()
    //   const _ref = await client.fetch(
    //     "*[_type == 'centerCap' && name=='Gloss Black'][0]._id"
    //   )
    //   return { _ref, _type: 'reference' }
    // },
  },
]

export default defineType({
  name: 'album',
  title: 'Albums',
  type: 'document',
  groups: [
    {
      name: 'content',
      title: 'Content',
      default: true,
    },
    seoGroup,
  ],
  initialValue: async (_props: any, context: any) => {
    const client = context.getClient({apiVersion: '2024-01-01'})
    const query = 'math::max(*[ _type == "album" ].albumId) + 1'
    const albumId = await client.fetch(query)
    const id = parseInt(albumId) || 1
    return {albumId: id, albumIdStr: `${id}`}
  },
  fields: [
    getPageInfo({ lockCanonical: true }),
    defineField({
      name: 'hide',
      title: 'Hide Album?',
      type: 'boolean',
      group: 'content',
      initialValue: true,
      validation: (rule) => rule.required(),
    }),
    defineField({
      name: 'albumId',
      title: 'Album ID',
      type: 'number',
      group: 'content',
      hidden: false,
      readOnly: false,
      validation: (rule) =>
        rule.required().custom(async (value, context) => {
          if (!value || !context.document?._id) return true

          const client = context.getClient({apiVersion: '2024-01-01'})
          const otherAlbumsUsingId = await client.fetch(
            `*[ _type == "album" && albumId == $albumId && _id != $docId]._id`,
            {albumId: value, docId: context.document._id},
          )
          if (!otherAlbumsUsingId?.length) return true

          // Handle draft/published state duplicates
          const documentId = context.document._id as string
          const documentIdNoDraft = documentId.startsWith('drafts.')
            ? documentId.slice(7)
            : documentId
          const cleanedIds = otherAlbumsUsingId.filter(
            (id: string) => id !== documentIdNoDraft && id !== documentId,
          )
          if (!cleanedIds?.length) return true

          return 'This ID is used for another album, please increment it by 1'
        }),
    }),
    defineField({
      name: 'albumIdStr',
      title: 'Album ID Search',
      type: 'string',
      group: 'content',
      hidden: true,
      readOnly: true,
    }),
    defineField({
      name: 'viewsCount',
      title: 'Views Count',
      type: 'number',
      group: 'content',
      initialValue: 0,
      readOnly: true,
    }),
    defineField({
      name: 'url',
      title: 'URL',
      type: 'string',
      group: 'content',
      options: { disabled: true },
      components: {
        input: AlbumURL,
      },
    }),
    defineField({
      name: 'vehicle',
      title: 'Vehicle',
      type: 'object',
      group: 'content',
      fields: [
        defineField({
          name: 'make',
          type: 'reference',
          to: [{ type: 'make' }],
        }),
        defineField({
          name: 'model',
          type: 'reference',
          to: [{ type: 'model' }],
        }),
        defineField({
          name: 'generation',
          type: 'reference',
          to: [{ type: 'generation' }],
        }),
        defineField({
          name: 'submodel',
          type: 'reference',
          to: [{ type: 'submodel' }],
        }),
      ],
      validation: (rule) =>
        rule.required().custom((vehicle) => {
          if (!vehicle?.make) {
            return 'Vehicle make is required'
          }
          if (!vehicle?.model) {
            return 'Vehicle model is required'
          }
          if (!vehicle?.generation) {
            return 'Vehicle generation is required'
          }
          return true
        }),
      components: {
        input: VehicleInput,
      },
    }),

    // Required attributes
    ...attributes.map(({ name, title, type = 'reference', typeto }) => {
      return defineField({
        name,
        title,
        type,
        group: 'content',
        ...(type === 'array'
          ? {
              of: [
                {
                  type: 'reference',
                  to: [{ type: typeto }],
                  options: {
                    disableNew: true,
                  },
                },
              ],
            }
          : { to: [{ type: typeto }] }),
        options: {
          disableNew: true,
        },
        validation: (rule) => rule.required(),
      })
    }),

    // Optional attributes
    ...attributesOpt.map(
      ({
        name,
        title,
        type = 'reference',
        typeto,
        options = {
          disableNew: true,
        },
        hidden = false,
        initialValue = false,
      }) => {
        return defineField({
          name,
          title,
          type,
          group: 'content',
          ...(type === 'array'
            ? {
                of: [
                  {
                    type: 'reference',
                    to: [{ type: typeto }],
                    options: {
                      disableNew: true,
                    },
                  },
                ],
              }
            : { to: [{ type: typeto }] }),
          options,
          ...(hidden ? { hidden } : {}),
          ...(initialValue !== false ? { initialValue } : {}),
        })
      }
    ),

    defineField({
      name: 'description',
      title: 'Description',
      type: 'text',
      group: 'content',
    }),
    defineField({
      name: 'photoCredit',
      title: 'Photo Credit',
      type: 'string',
      group: 'content',
    }),
    defineField({
      name: 'images',
      title: 'Images',
      type: 'array',
      group: 'content',
      of: [
        {
          type: 'image',
          options: {
            hotspot: true,
          },
        },
      ],
      validation: (rule) =>
        rule.required()
        // TODO: Add generateImageAltTags validation after migration
        // .custom(async (images, context) => {
        //   await generateImageAltTags(images, context.document)
        //   return true
        // }),
    }),
    defineField({
      name: 'dateCreated',
      title: 'Date Created',
      type: 'date',
      group: 'content',
      initialValue: new Date().toISOString().split('T')[0],
      validation: (rule) => rule.required(),
    }),
  ],
  preview: {
    select: {
      make: 'vehicle.make.name',
      model: 'vehicle.model.name',
      generation: 'vehicle.generation.name',
      date: 'dateCreated',
      images: 'images',
      media: 'images.0.asset',
      hide: 'hide',
      albumId: 'albumId',
      color: 'color.name',
      diameter: 'diameter.name',
      design: 'design.name',
      finish: 'finish.name',
    },
    prepare(selection) {
      const {
        make = '',
        model = '',
        generation = '',
        date = '',
        images = [],
        hide = false,
        albumId = 0,
        media = false,
        color = '',
        diameter = '',
        design = '',
        finish = '',
      } = selection
      const [dt = ''] = (date || '').split('T')

      const title = !`${make}${model}${generation}`.trim().length
        ? 'New Album'
        : `${
            hide ? '[Hidden] ' : ''
          }${albumId} - ${color} ${make} ${model} ${generation}`

      const label = !`${diameter}${design}${finish}`.trim().length
        ? ''
        : `${diameter} ${design} in ${finish} - `

      return {
        title,
        subtitle: `${label}${images?.length || 0}p | ${dt}`,
        media,
      }
    },
  },
})